/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { videoPilot } from '../models/video/video-pilot';

/**
 * Intersection options container
 *
 * **Available Options:**
 *  - `root`:
 *    - The element that is used as the viewport for checking visibility of the target. Must be the ancestor of the target.
 *    - Defaults to the browser viewport if not specified or if `null`.
 *
 *  - `rootMargin`:
 *    - Margin around the root. Can have values similar to the CSS margin property, e.g. `10px 20px 30px 40px` _(top, right, bottom, left)_.
 *    - The values can be percentages. This set of values serves to grow or shrink each side of the root element's bounding box before computing intersections.
 *    - Defaults to all zeros `0px`.
 *
 *  - `threshold`:
 *    - Either a single number or an array of numbers which indicate at what percentage of the target's visibility the observer's callback should be executed.
 *    - If you only want to detect when visibility passes the 50% mark, you can use a value of `0.5`.
 *    - If you want the callback to run every time visibility passes another `25%`, you would specify the array `[0, 0.25, 0.5, 0.75, 1]`.
 *    - The default is `0` _(meaning as soon as even one pixel is visible, the callback will be run)_.
 *    - A value of `1.0` means that the threshold isn't considered passed until every pixel is visible.
 *
 * **Available Callbacks:** All callbacks wil recieve an `IntersectionObserverEntry` as arguments
 *  - `intersect`: Trigger when element intersection change
 *  - `enter`: Trigger when element visibility ratio larger than configured `threshold`
 *  - `leave` Trigger when element visibility ratio lower or equal configured `threshold`
 */
export const intersectionOptions = {
  /**
   * This directive has no default options
   */
  default: {},

  /**
   * Slick preset used to Slick slider container size.
   * This preset should use with `slick` directive which render in a hidden container.
   *
   * @example
   *
   * ```html
   *  <div amb-slick amb-intersection="slick"></div>
   * ```
   */
  slick: {
    threshold: [0, 1],

    /**
     * Trigger when Slick slider container visible ratio larger than 0.
     *
     * @param {IntersectionObserverEntry} entry The intersection entry
     */
    enter(entry) {
      entry.target.slick?.resize();
    },
  },

  /**
   * Video preset used to controls video playback programmatically.
   *
   * @example
   *
   * ```html
   *  <div amb-video="16401445" amb-intersection="video"></div>
   * ```
   */
  video: {
    threshold: 0.25,

    /**
     * Trigger when video element visible ratio larger than configured `threshold`
     *
     * @param {IntersectionObserverEntry} entry The intersection entry
     */
    enter(entry) {
      videoPilot.enter(entry.target, ['init', 'autoplay']);
    },

    /**
     * Trigger when video element visible ratio lower or equal configured `threshold`
     *
     * @param {IntersectionObserverEntry} entry The intersection entry
     */
    leave(entry) {
      videoPilot.leave(entry.target, ['autopause']);
    }
  },

  /**
   * Init video preset used to initialize video when user enter the video.
   *
   * @example
   *
   * ```html
   *  <div amb-video="16401445" amb-intersection="init_video"></div>
   * ```
   */
  init_video: {
    threshold: 0.1,

    /**
     * Trigger when video element visible ratio larger than configured `threshold`
     *
     * @param {IntersectionObserverEntry} entry The intersection entry
     */
    enter(entry) {
      videoPilot.enter(entry.target, ['init']);
    },

    /**
     * Trigger when video element visible ratio lower or equal configured `threshold`
     *
     * @param {IntersectionObserverEntry} entry The intersection entry
     */
    leave(entry) {
      videoPilot.leave(entry.target, ['autopause']);
    }
  }
};
